"use client";

import {
  LegalDocumentPage,
  LegalTextSection,
} from "@/components/legal/LegalDocumentPage";
import { useLanguage } from "@/context/LanguageContext";

export default function BussinessAggreement() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      title: "Obchodní podmínky",
      subtitle:
        "platné pro službu Hlídací pes na webu softiq.cz/hlidaci-pes od 2.1.2025",
      sections: [
        {
          title: "Provozovatel",
          paragraphs: [
            "Službu Hlídací pes provozuje:\nIng. Martin Švejda\nIČO: 21086231\nSídlo: Janského 559/13, 779 00, Olomouc – Povel\nE-mail: info@softiq.cz\n(dále jen „Provozovatel“).",
          ],
        },
        {
          title: "Popis služby",
          paragraphs: [
            "Provozovatel poskytuje registrovaným uživatelům službu zasílání e-mailových upozornění na nové realitní inzeráty odpovídající jejich nastaveným kritériím.",
          ],
          listIntro:
            "Služba nabízí tři úrovně členství: Základní, Standardní a Prémiové. Úrovně se liší:",
          list: [
            "maximálním počtem hlídacích psů,",
            "dostupností historie inzerátů,",
            "možnostmi detailní filtrace,",
            "frekvencí reportování.",
          ],
          text: "Úroveň Základní je k dispozici zdarma po omezenou dobu. Po uplynutí této doby musí uživatel upgradovat členství, jinak bude jeho účet automaticky pozastaven.",
        },
        {
          title: "Nastavení a správa hlídacích psů",
          text: "V rámci služby si uživatel může nastavit hlídací psy podle zvolené úrovně členství. Může je kdykoliv deaktivovat ve svém účtu a své členství upravovat nebo zrušit. Platby za aktuálně předplacené členství nejsou vratné, ani poměrnou částí.",
        },
        {
          title: "Vznik a aktivace členství",
          text: "Registrací na webu vzniká mezi Uživatelem a Provozovatelem smluvní vztah, který umožňuje bezplatné využívání služby po omezenou dobu.",
        },
        {
          title: "Fakturace",
          text: "Uživatel obdrží fakturu za provedenou platbu. Faktura je vystavena souhrnně za poslední tři platební období, na vyžádání dříve. Faktura je zasílána subjektům, kteří řádně vyplnili fakturační údaje.",
        },
        {
          title: "Zrušení služby",
          text: "Uživatel může deaktivovat e-mailová upozornění odstraněním všech hlídacích psů, čímž služba zůstane aktivní. Pro úplné zrušení účtu je nutné kliknout na „Deaktivace účtu“. Po deaktivaci se nelze znovu přihlásit.",
        },
        {
          title: "Omezení služby",
          text: "Provozovatel nezaručuje 100% doručení všech inzerátů. Nenese odpovědnost za obsah a dostupnost inzerátů na externích serverech.",
        },
        {
          title: "Odstoupení od smlouvy",
          text: "Uživatel může odstoupit od smlouvy do 14 dnů e-mailem. Musí uvést email účtu. Peníze se nevrací, pokud již byla služba využita.",
        },
        {
          title: "Reklamační řád",
          label: "1. Úvodní ustanovení",
          text: "Tento řád se vztahuje na poskytování služby hlídacího psa. Reklamace znamená nefunkční nebo chybně poskytnutou službu.",
        },
        {
          label: "2. Podání reklamace",
          listIntro: "Reklamace se podává na info@softiq.cz a musí obsahovat:",
          list: [
            "jméno a kontakt,",
            "přihlašovací e-mail,",
            "identifikaci hlídacího psa,",
            "popis závady,",
            "důkazy (např. screenshoty).",
          ],
        },
        {
          title: "Podmínky předplatného",
          paragraphs: [
            "Předplatné se sjednává na dobu neurčitou s měsíční automatickou obnovou. Po provedení první platby je předplatitelovi každý měsíc automaticky strhávána částka odpovídající zvolenému předplatnému, dokud si svůj účet nezruší. Předplatitel může své předplatné kdykoli ukončit primárně ve svém uživatelském účtu, nebo prostřednictvím e-mailu na adrese info@softiq.cz. Ukončení předplatného se projeví od následujícího zúčtovacího období. Již uhrazené předplatné za aktuální období není možné vrátit. Pokud si předplatitel nepřeje pokračovat v předplatném, je nutné jej aktivně zrušit. V opačném případě bude předplatné automaticky obnovováno a platby budou dále strhávány.",
          ],
          listIntro: "Předplatné je měsíčně obnovováno automaticky:",
          list: [
            "Professional – 249 Kč",
            "Premium – 89 Kč",
            "Standard – 49 Kč",
          ],
          text: "Opakovaná platba probíhá automaticky bez předchozího avíza předplatitele, a to ve výši a periodicitě uvedené při založení objednávky, maximálně do konce platnosti platební karty. V případě, že by opakovaná platba měla být v jiné výši, než bylo uvedeno v objednávce, bude předplatitel o takové změně informován s předstihem prostřednictvím e-mailu.\n\nPlatební údaje jsou uloženy na straně platební brány GoPay, která s údaji nakládá podle mezinárodního bezpečnostního standardu PCI DSS Level 1 (jedná se o nejvyšší úroveň datové bezpečnosti v sektoru zpracovávání platebních karet).",
        },
      ] as LegalTextSection[],
    },
    en: {
      title: "Terms and Conditions",
      subtitle:
        "valid for the Watchdog service at softiq.cz/hlidaci-pes from 2 January 2025",
      sections: [
        {
          title: "Operator",
          paragraphs: [
            "The Watchdog service is operated by:\nIng. Martin Švejda\nCompany ID: 21086231\nRegistered office: Janského 559/13, 779 00, Olomouc – Povel\nE-mail: info@softiq.cz\n(hereinafter the “Operator”).",
          ],
        },
        {
          title: "Service Description",
          paragraphs: [
            "The Operator provides registered users with a service for sending e-mail alerts about new real estate listings matching their configured criteria.",
          ],
          listIntro:
            "The service offers three membership levels: Basic, Standard, and Premium. The levels differ by:",
          list: [
            "the maximum number of watchdogs,",
            "access to listing history,",
            "advanced filtering options,",
            "reporting frequency.",
          ],
          text: "The Basic level is available free of charge for a limited time. After that period expires, the user must upgrade the membership, otherwise the account will be automatically suspended.",
        },
        {
          title: "Watchdog Setup and Management",
          text: "Within the service, the user may set up watchdogs according to the selected membership level. The user may deactivate them at any time in the account and may change or cancel the membership. Payments for the currently prepaid membership are non-refundable, including any proportional part.",
        },
        {
          title: "Creation and Activation of Membership",
          text: "Registration on the website creates a contractual relationship between the User and the Operator, allowing free use of the service for a limited time.",
        },
        {
          title: "Billing",
          text: "The user receives an invoice for the completed payment. The invoice is issued collectively for the last three billing periods, or earlier upon request. Invoices are sent to entities that have properly completed their billing details.",
        },
        {
          title: "Cancellation of the Service",
          text: "The user may deactivate e-mail alerts by removing all watchdogs, while the service remains active. To fully cancel the account, it is necessary to click “Account deactivation”. After deactivation, logging in again is not possible.",
        },
        {
          title: "Service Limitations",
          text: "The Operator does not guarantee 100% delivery of all listings. The Operator is not responsible for the content and availability of listings on external servers.",
        },
        {
          title: "Withdrawal from the Contract",
          text: "The user may withdraw from the contract within 14 days by e-mail. The account e-mail must be provided. Money is not refunded if the service has already been used.",
        },
        {
          title: "Complaints Procedure",
          label: "1. Introductory Provisions",
          text: "This procedure applies to the provision of the watchdog service. A complaint means a non-functional or incorrectly provided service.",
        },
        {
          label: "2. Filing a Complaint",
          listIntro: "A complaint is filed at info@softiq.cz and must include:",
          list: [
            "name and contact details,",
            "login e-mail,",
            "identification of the watchdog,",
            "description of the defect,",
            "evidence, for example screenshots.",
          ],
        },
        {
          title: "Subscription Terms",
          paragraphs: [
            "The subscription is concluded for an indefinite period with automatic monthly renewal. After the first payment is made, the subscriber is automatically charged each month for the amount corresponding to the selected subscription until the account is cancelled. The subscriber may cancel the subscription at any time primarily in the user account, or by e-mail at info@softiq.cz. The cancellation takes effect from the following billing period. Subscription fees already paid for the current period are non-refundable. If the subscriber does not wish to continue the subscription, it must be actively cancelled. Otherwise, the subscription will be renewed automatically and payments will continue to be charged.",
          ],
          listIntro: "The subscription is renewed automatically each month:",
          list: [
            "Professional – CZK 249",
            "Premium – CZK 89",
            "Standard – CZK 49",
          ],
          text: "The recurring payment is made automatically without prior notice to the subscriber, in the amount and frequency stated when the order was created, at most until the payment card expires. If a recurring payment should be made in a different amount than stated in the order, the subscriber will be informed about the change in advance by e-mail.\n\nPayment details are stored by the GoPay payment gateway, which handles the data according to the international PCI DSS Level 1 security standard, the highest level of data security in the payment card processing sector.",
        },
      ] as LegalTextSection[],
    },
  }[language];

  return (
    <LegalDocumentPage
      title={copy.title}
      subtitle={copy.subtitle}
      sections={copy.sections}
      className="min-h-screen px-6 py-10 md:px-10 font-sans"
    />
  );
}
